import { type ReactNode, useEffect, useMemo, useState } from "react"

import Portal from "@forento/shared/components/Portal"
import { cn } from "@forento/shared/utilities/tailwind"

import { ArrowIcon, CloseIcon } from "~/themes/school/components/Icon"

interface UseImageCarousel {
	showIndex(index: number): void
	element: ReactNode
}
export default function useImageCarousel(images: { id: number; filePath: string }[]): UseImageCarousel {
	const [isOpen, setOpen] = useState(false)
	const [currentImageIndex, setCurrentImageIndex] = useState(0)

	useEffect(() => {
		if (!isOpen) return

		const abortController = new AbortController()

		window.addEventListener(
			"keydown",
			event => {
				if (event.key === "Escape") setOpen(false)
				if (event.key === "ArrowLeft" && currentImageIndex > 0) setCurrentImageIndex(currentImageIndex - 1)
				if (event.key === "ArrowRight" && currentImageIndex < images.length - 1)
					setCurrentImageIndex(currentImageIndex + 1)
			},
			{ signal: abortController.signal },
		)

		return () => abortController.abort()
	}, [currentImageIndex, images.length, isOpen])

	return useMemo(
		() => ({
			showIndex(index) {
				setCurrentImageIndex(index)
				setOpen(true)
			},
			element: (
				<Portal>
					<div
						className={cn(
							"pointer-events-none fixed inset-0 flex items-center justify-center transition-all duration-100",
							isOpen && "pointer-events-auto bg-black/50",
						)}
					>
						<div className={cn("relative scale-0 transition-all duration-100", isOpen && "scale-100")}>
							<button
								type="button"
								className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
								onClick={() => setOpen(false)}
								aria-label="Close image carousel"
							>
								<CloseIcon className="h-6 w-6 text-white" />
							</button>
							{currentImageIndex > 0 && (
								<button
									type="button"
									className="absolute left-6 top-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
									onClick={() => setCurrentImageIndex(currentImageIndex - 1)}
									aria-label="Previous image"
								>
									<ArrowIcon direction="left" className="h-6 w-6 text-white" />
								</button>
							)}
							{currentImageIndex < images.length - 1 && (
								<button
									type="button"
									className="absolute right-6 top-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
									onClick={() => setCurrentImageIndex(currentImageIndex + 1)}
									aria-label="Next image"
								>
									<ArrowIcon direction="right" className="h-6 w-6 text-white" />
								</button>
							)}
							<img
								src={images[currentImageIndex].filePath}
								alt={`Post image ${currentImageIndex + 1} in carousel`}
								className="h-auto max-h-[90vh] w-full max-w-screen-lg rounded-lg object-contain"
							/>
						</div>
					</div>
				</Portal>
			),
		}),
		[currentImageIndex, images, isOpen],
	)
}
